import { action, makeObservable, observable, runInAction } from 'mobx';
import { differenceInMilliseconds } from 'date-fns';
import { throttle } from 'es-toolkit';

interface IdleStoreConfig {
  events?: string[];
}

const BASE_STORAGE_KEY = 'app_idle_activity';

const MAX_IDLE_TIME = 30 * 60 * 1000; // 30 minutes in milliseconds

export class IdleStore {
  private timeout: number = MAX_IDLE_TIME;
  private userStorageKey: string | null = null;
  private events: string[];
  private timeoutId: number | null = null;
  private checkInterval: number | null = null;

  @observable lastActivityDate: Date = new Date();
  @observable isActive: boolean = false;
  @observable isIdle: boolean = false;
  @observable remainingTime: number = 0;

  constructor(config?: Partial<IdleStoreConfig>) {
    makeObservable(this);

    this.events = config?.events || ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown', 'focus'];
  }

  @action
  setUserStorageKey = (username: string) => {
    this.userStorageKey = `${BASE_STORAGE_KEY}_${username}`;
  };

  @action
  start = () => {
    if (this.isActive) return;

    this.isActive = true;
    this.isIdle = false;

    this.updateLastActivity();
    this.attachEventListeners();
    this.startTimeoutCheck();
    this.startCrossTabSync();
  };

  @action
  stop = () => {
    if (!this.isActive) return;

    this.isActive = false;

    this.detachEventListeners();
    this.clearTimeoutCheck();
    this.stopCrossTabSync();
  };

  @action
  reset = () => {
    this.updateLastActivity();
    this.isIdle = false;
  };

  @action
  removeActivity(): void {
    if (this.userStorageKey) { 
      localStorage.removeItem(this.userStorageKey);
    }
  };

  private updateLastActivity = () => {
    runInAction(() => {
      this.lastActivityDate = new Date();
    });

    this.userStorageKey && localStorage.setItem(this.userStorageKey, this.lastActivityDate.toISOString());
    this.resetTimeout();
  };

  private resetTimeout = () => {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = window.setTimeout(() => {
      this.handleIdle();
    }, this.timeout);
  };

  private handleIdle = () => {
    runInAction(() => {
      this.isIdle = true;
    });
    this.stop();
  };

  private attachEventListeners = (): void => {
    const highFrequencyEvents = ['mousemove', 'scroll'];
    const lowFrequencyEvents = this.events.filter((event) => !highFrequencyEvents.includes(event));

    highFrequencyEvents.forEach((event) => {
      if (this.events.includes(event)) {
        window.addEventListener(event, throttle(this.updateLastActivity, 1000), true);
      }
    });

    lowFrequencyEvents.forEach((event) => {
      window.addEventListener(event, this.updateLastActivity, true);
    });
  };

  private detachEventListeners = () => {
    this.events.forEach((event) => {
      window.removeEventListener(event, this.updateLastActivity, true);
    });
  };

  private startTimeoutCheck = () => {
    this.checkInterval = window.setInterval(
      throttle(() => {
        const storedTime = this.userStorageKey && localStorage.getItem(this.userStorageKey);
        if (storedTime) {
          const lastActivityDate = new Date(storedTime);
          const now = new Date();
          const timeSinceLastActivity = differenceInMilliseconds(now, lastActivityDate);

          runInAction(() => {
            this.remainingTime = Math.max(0, this.timeout - timeSinceLastActivity);
            this.lastActivityDate = lastActivityDate;
          });

          if (timeSinceLastActivity >= this.timeout) {
            this.handleIdle();
          }
        }
      }, 5000),
      1000,
    );
  };

  private clearTimeoutCheck = () => {
    if (this.checkInterval !== null) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  };

  private startCrossTabSync = () => {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    window.addEventListener('storage', this.handleStorageEvent);
  };

  private stopCrossTabSync = () => {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    window.removeEventListener('storage', this.handleStorageEvent);
  };

  private handleVisibilityChange = () => {
    if (!document.hidden) {
      const storedTime = this.userStorageKey && localStorage.getItem(this.userStorageKey);
      if (storedTime) {
        const lastActivityDate = new Date(storedTime);
        const now = new Date();
        const timeSinceLastActivity = differenceInMilliseconds(now, lastActivityDate);

        if (timeSinceLastActivity >= this.timeout) {
          this.handleIdle();
        } else {
          const remainingTime = this.timeout - timeSinceLastActivity;
          if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
          }
          this.timeoutId = window.setTimeout(() => {
            this.handleIdle();
          }, remainingTime);
        }
      }
    }
  };

  private handleBeforeUnload = () => {
    this.userStorageKey && localStorage.setItem(this.userStorageKey, this.lastActivityDate.toISOString());
  };

  private handleStorageEvent = (event: StorageEvent) => {
  if (event.key === this.userStorageKey && event.newValue) {
    const lastActivityDate = new Date(event.newValue);
    
    runInAction(() => {
      this.lastActivityDate = lastActivityDate;
      const now = new Date();
      const timeSinceLastActivity = differenceInMilliseconds(now, lastActivityDate);
      this.remainingTime = Math.max(0, this.timeout - timeSinceLastActivity);
    });

    if (this.isIdle) {
      runInAction(() => {
        this.isIdle = false;
      });
    }
  }
};
}
